// Styles de base des modales et de l'import Excel, partagés avec le puits de commandes
import '../../TruckAssignments/manual-assignment-modal.css';
import '../../../redesign-v4/pages/OrdersPool/orders-pool.css';

import {
  faDownload,
  faFileExcel,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';

import useImportRecipients from '../../../api/addresses/useImportRecipients';

const STATUS_LABELS = {
  OK: 'Prêt',
  IGNORED: 'Code déjà utilisé : ignoré',
};

const plural = (count, singular, pluralForm) =>
  `${count} ${count > 1 ? pluralForm : singular}`;

/**
 * Libellé de l'état d'une ligne : toutes ses erreurs si elle est refusée, sinon
 * son statut suivi de ses avertissements
 *
 * @param {object} row - Ligne de l'aperçu ou du compte rendu
 * @param {boolean} isResult - Compte rendu de la création (« Créé » au lieu de « Prêt »)
 * @returns {string} - Le libellé
 */
const rowLabel = (row, isResult) => {
  if (row.status === 'ERROR') return `⚠ ${row.errors.join(' · ')}`;

  return [
    row.status === 'OK' ? (isResult ? 'Créé' : STATUS_LABELS.OK) : null,
    // L'avertissement d'un code déjà utilisé redit le statut
    ...(row.status === 'IGNORED'
      ? [STATUS_LABELS.IGNORED]
      : row.warnings || []),
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * Modale « Importer des destinataires » : modèle à télécharger, dépôt du
 * fichier, aperçu sans rien créer, confirmation, puis compte rendu. Les lignes
 * en erreur ne bloquent pas la confirmation : les autres sont créées.
 *
 * Une adresse que la géolocalisation ne trouve pas crée quand même son
 * destinataire, sans position : le compte rendu le dit et mène au filtre
 * « Non géolocalisés » de la page.
 *
 * @param {object} props - Les propriétés
 * @param {boolean} props.isOpen - Ouverture
 * @param {Function} props.onClose - Fermeture
 * @param {Function} props.onImported - Appelé après une confirmation, avec showNotGeolocated
 * @returns {JSX.Element|null} - La modale
 */
export default function ImportRecipientsModal({ isOpen, onClose, onImported }) {
  const {
    preview,
    result,
    isUploading,
    isConfirming,
    uploadFile,
    confirmImport,
    reset,
    downloadTemplate,
  } = useImportRecipients();

  const [fileName, setFileName] = useState('');
  const inputRef = useRef(null);

  // Chaque ouverture repart d'un écran vide
  useEffect(() => {
    if (!isOpen) return;
    reset();
    setFileName('');
    if (inputRef.current) inputRef.current.value = '';
  }, [isOpen, reset]);

  if (!isOpen) return null;

  const isBusy = isUploading || isConfirming;
  const report = (result || preview)?.report;
  const rows = (result || preview)?.rows || [];

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    reset();
    setFileName(file.name);
    await uploadFile(file);
    // Le même fichier, corrigé, peut être redéposé
    event.target.value = '';
  };

  const handleConfirm = async () => {
    const data = await confirmImport();
    if (data) onImported?.({ showNotGeolocated: false });
  };

  const close = () => {
    if (!isConfirming) onClose();
  };

  return (
    <div className="modal-overlay" onClick={close}>
      <div
        className="modal-container op-import-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Importer des destinataires</h2>
            <p className="modal-subtitle">
              Une ligne du fichier = un destinataire. Un code déjà utilisé par
              un destinataire actif est ignoré : modifiez-le depuis sa fiche.
            </p>
          </div>
          <button
            className="modal-close-btn"
            onClick={close}
            disabled={isConfirming}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-body">
          {!result && (
            <div className="op-import-modal__actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={downloadTemplate}
              >
                <FontAwesomeIcon icon={faDownload} /> Télécharger le modèle
              </button>
              <label
                className={`btn-primary op-import-modal__file ${
                  isBusy ? 'op-import-modal__file--disabled' : ''
                }`}
              >
                <FontAwesomeIcon icon={faFileExcel} />{' '}
                {isUploading ? 'Lecture du fichier…' : 'Choisir un fichier'}
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  disabled={isBusy}
                  onChange={handleFile}
                  hidden
                />
              </label>
              {fileName && (
                <span className="op-import-modal__filename" title={fileName}>
                  {fileName}
                </span>
              )}
            </div>
          )}

          {report && (
            <>
              <div className="op-import-modal__summary">
                {result ? (
                  <>
                    <span className="op-import-modal__count--ok">
                      {plural(
                        report.created,
                        'destinataire créé',
                        'destinataires créés'
                      )}
                    </span>
                    {report.not_geolocated > 0 && (
                      <span className="op-import-modal__count--error">
                        {plural(
                          report.not_geolocated,
                          'non géolocalisé',
                          'non géolocalisés'
                        )}{' '}
                        : à placer depuis leur fiche
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <span>
                      {plural(report.rows_read, 'ligne lue', 'lignes lues')}
                    </span>
                    <span className="op-import-modal__count--ok">
                      {plural(
                        report.to_create,
                        'destinataire à créer',
                        'destinataires à créer'
                      )}
                    </span>
                  </>
                )}
                {report.ignored > 0 && (
                  <span>{plural(report.ignored, 'ignoré', 'ignorés')}</span>
                )}
                {report.in_error > 0 && (
                  <span className="op-import-modal__count--error">
                    {plural(
                      report.in_error,
                      'ligne en erreur',
                      'lignes en erreur'
                    )}
                  </span>
                )}
                {!result && (
                  <span className="op-import-modal__hint">
                    Rien n'est créé tant que vous n'avez pas confirmé. Les
                    adresses sont géolocalisées à la création.
                  </span>
                )}
              </div>

              <div className="op-import-modal__table">
                <table>
                  <thead>
                    <tr>
                      <th>Ligne</th>
                      <th>Code</th>
                      <th>Nom</th>
                      <th>État</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.row_number}
                        className={`op-import-modal__row--${row.status.toLowerCase()}`}
                      >
                        <td>{row.row_number}</td>
                        <td>{row.address_key || '—'}</td>
                        <td>{row.name || '—'}</td>
                        <td>{rowLabel(row, Boolean(result))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          {result ? (
            <>
              {result.report.not_geolocated > 0 && (
                <button
                  className="btn-secondary"
                  onClick={() => {
                    onImported?.({ showNotGeolocated: true });
                    onClose();
                  }}
                >
                  Voir les non géolocalisés
                </button>
              )}
              <button className="btn-primary" onClick={onClose}>
                Fermer
              </button>
            </>
          ) : (
            <>
              <button
                className="btn-secondary"
                onClick={close}
                disabled={isConfirming}
              >
                Annuler
              </button>
              <button
                className="btn-primary"
                onClick={handleConfirm}
                disabled={!preview || preview.report.to_create === 0 || isBusy}
                title={
                  preview && preview.report.to_create === 0
                    ? 'Aucune ligne de ce fichier ne peut être créée'
                    : undefined
                }
              >
                {isConfirming
                  ? 'Création et géolocalisation…'
                  : `Créer ${plural(
                      preview?.report.to_create || 0,
                      'destinataire',
                      'destinataires'
                    )}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
